import { useEffect, useMemo, useState } from 'react';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  LoaderIcon,
  PencilIcon,
  PlusIcon,
  RegenerateUuidIcon,
  TrashIcon,
  EyeIcon,
  PauseIcon,
  PlayIcon,
} from '@/components/icons';
import { channelApi } from '@/api/channel.api';
import { ApiError } from '@/api/errors';
import { toast } from 'sonner';
import { jobApi } from '@/api/job.api';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { Skeleton } from '@/components/ui/skeleton';
import { Tabs } from '@/components/ui/tabs';
import {
  Table,
  TableHeader,
  TableBody,
  TableRow,
  TableHead,
  TableCell,
} from '@/components/ui/table';
import {
  AlertDialog,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogCancel,
  AlertDialogAction,
} from '@/components/ui/alert-dialog';
import { SlideOver } from '@/components/ui/slide-over';
import { create } from '@bufbuild/protobuf';
import { ListRequestSchema } from '@/proto/agentplatform/httpv2/common_pb';
import type { ChannelAccount, ChannelUserBinding } from '@/proto/agentplatform/v1/channel_pb';
import {
  ChannelUserBindingSchema,
  ToolPermissionsSchema,
  ToolPermissionRuleSchema,
} from '@/proto/agentplatform/v1/channel_pb';
import {
  ChannelAccountBindingMode,
  ChannelType,
  AccountStatus,
  ChatType,
} from '@/proto/agentplatform/v1/channel_pb';
import {
  ChannelSessionSchema,
  ChannelP2PSessionSchema,
  ChannelSessionStateSchema,
} from '@/proto/agentplatform/v1/channel_pb';
import { JobStatus, JobRunStatus, JobType } from '@/proto/agentplatform/v1/job_pb';
import { cn } from '@/lib/utils';

function formatTimestamp(ts: number | bigint | undefined) {
  if (ts == null || ts === 0n) return '-';
  const n = Number(ts);
  if (!n) return '-';
  return new Date(n / 1000000).toLocaleString('zh-CN', {
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
    hour12: false,
  });
}

function channelTypeLabel(t: ChannelType) {
  switch (t) {
    case ChannelType.FEISHU:
      return '飞书';
    case ChannelType.QQ:
      return 'QQ';
    default:
      return ChannelType[t] ?? '—';
  }
}

function bindingModeLabel(m: ChannelAccountBindingMode) {
  switch (m) {
    case ChannelAccountBindingMode.TEMPLATE:
      return '模板绑定';
    case ChannelAccountBindingMode.INSTANCE:
      return '实例绑定';
    default:
      return '—';
  }
}

function AccountStatusBadge({ status }: { status: AccountStatus }) {
  if (status === AccountStatus.ONLINE) {
    return (
      <Badge variant="outline" className="text-green-600 border-green-300">
        在线
      </Badge>
    );
  }
  return (
    <Badge variant="outline" className="text-red-600 border-red-300">
      离线
    </Badge>
  );
}

function InfoItem({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <div className="space-y-1.5">
      <span className="text-xs font-medium text-muted-foreground uppercase tracking-wide">
        {label}
      </span>
      <div className="flex items-center gap-2">
        <div className="flex-1">{children}</div>
      </div>
    </div>
  );
}

function EmptyState({ title, description }: { title: string; description: string }) {
  return (
    <div className="flex flex-col items-center justify-center py-12 text-center">
      <div className="w-12 h-12 rounded-full bg-muted flex items-center justify-center mb-4">
        <svg
          className="w-6 h-6 text-muted-foreground"
          fill="none"
          viewBox="0 0 24 24"
          stroke="currentColor"
          strokeWidth={2}
        >
          <path
            strokeLinecap="round"
            strokeLinejoin="round"
            d="M20 13V6a2 2 0 00-2-2H6a2 2 0 00-2 2v7m16 0v5a2 2 0 01-2 2H6a2 2 0 01-2-2v-5m16 0h-2.586a1 1 0 00-.707.293l-2.414 2.414a1 1 0 01-.707.293h-3.172a1 1 0 01-.707-.293l-2.414-2.414A1 1 0 006.586 13H4"
          />
        </svg>
      </div>
      <h3 className="text-sm font-medium mb-1">{title}</h3>
      <p className="text-xs text-muted-foreground">{description}</p>
    </div>
  );
}

function channelCapabilitiesImplyGroupChat(
  capabilities: { chatTypes: ChatType[] } | undefined
): boolean | null {
  const types = capabilities?.chatTypes;
  if (types == null || types.length === 0) return null;
  return types.includes(ChatType.GROUP);
}

function channelTypeConventionallySupportsGroup(t: ChannelType): boolean {
  return t === ChannelType.FEISHU;
}

// ─── Overview Tab ──────────────────────────────────────────────

function OverviewTab({ account }: { account: ChannelAccount }) {
  const { data: channelsResponse } = useQuery({
    queryKey: ['channels', 'account_detail_caps'] as const,
    queryFn: () => channelApi.listChannels(create(ListRequestSchema, { page: 1, pageSize: 200 })),
  });

  const channelSupportsGroup = useMemo(() => {
    const row = channelsResponse?.data?.find((ch) => ch.type === account.type);
    const inferred = channelCapabilitiesImplyGroupChat(row?.capabilities);
    if (inferred !== null) return inferred;
    return channelTypeConventionallySupportsGroup(account.type);
  }, [channelsResponse?.data, account.type]);

  const cp = account.chatPolicy;

  return (
    <div className="grid gap-6">
      <Card>
        <CardHeader className="pb-3">
          <CardTitle className="text-base flex items-center gap-2">
            <svg
              className="w-4 h-4"
              fill="none"
              viewBox="0 0 24 24"
              stroke="currentColor"
              strokeWidth={2}
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                d="M20 7l-8-4-8 4m16 0l-8 4m8-4v10l-8 4m0-10L4 7m8 4v10M4 7v10l8 4"
              />
            </svg>
            基本信息
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <InfoItem label="名称">
              <span className="font-medium">{account.name || '—'}</span>
            </InfoItem>
            <InfoItem label="渠道类型">
              <Badge variant="secondary" className="text-xs font-sans font-medium px-2 py-0">
                {channelTypeLabel(account.type)}
              </Badge>
            </InfoItem>
            <InfoItem label="状态">
              <AccountStatusBadge status={account.status} />
            </InfoItem>
            <InfoItem label="绑定模式">
              <Badge variant="outline">{bindingModeLabel(account.bindingMode)}</Badge>
            </InfoItem>
            <InfoItem label="禁用">
              <span
                className={
                  account.disabled
                    ? 'text-amber-700 dark:text-amber-500'
                    : 'text-emerald-700 dark:text-emerald-500'
                }
              >
                {account.disabled ? '是' : '否'}
              </span>
            </InfoItem>
            <InfoItem label="绑定目标">
              <span className="font-mono text-xs break-all">{account.agentId || '—'}</span>
            </InfoItem>
          </div>
        </CardContent>
      </Card>

      <Card>
        <CardHeader className="pb-3">
          <CardTitle className="text-base flex items-center gap-2">
            <svg
              className="w-4 h-4"
              fill="none"
              viewBox="0 0 24 24"
              stroke="currentColor"
              strokeWidth={2}
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z"
              />
            </svg>
            凭证
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <InfoItem label="应用 ID">
              <span className="font-mono text-xs break-all">
                {account.credential?.appId || '—'}
              </span>
            </InfoItem>
            <InfoItem label="应用密钥">
              <span className="font-mono text-xs">••••••••</span>
            </InfoItem>
          </div>
        </CardContent>
      </Card>

      <Card>
        <CardHeader className="pb-3">
          <CardTitle className="text-base flex items-center gap-2">
            <svg
              className="w-4 h-4"
              fill="none"
              viewBox="0 0 24 24"
              stroke="currentColor"
              strokeWidth={2}
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                d="M8 12h.01M12 12h.01M16 12h.01M21 12c0 4.418-4.03 8-9 8a9.863 9.863 0 01-4.255-.949L3 20l1.395-3.72C3.512 15.042 3 13.574 3 12c0-4.418 4.03-8 9-8s9 3.582 9 8z"
              />
            </svg>
            聊天策略
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <InfoItem label="禁用单聊">
              <span
                className={
                  cp?.disableP2p
                    ? 'text-amber-700 dark:text-amber-500'
                    : 'text-emerald-700 dark:text-emerald-500'
                }
              >
                {cp?.disableP2p ? '是' : '否'}
              </span>
            </InfoItem>
            <InfoItem label="单聊白名单">
              <span className="text-sm">
                {cp?.p2pAllowFrom?.length ? cp.p2pAllowFrom.join(', ') : '不限制'}
              </span>
            </InfoItem>
            {channelSupportsGroup && (
              <>
                <InfoItem label="禁用群聊">
                  <span
                    className={
                      cp?.disableGroup
                        ? 'text-amber-700 dark:text-amber-500'
                        : 'text-emerald-700 dark:text-emerald-500'
                    }
                  >
                    {cp?.disableGroup ? '是' : '否'}
                  </span>
                </InfoItem>
                <InfoItem label="群聊白名单">
                  <span className="text-sm">
                    {cp?.groupAllowFrom?.length ? cp.groupAllowFrom.join(', ') : '不限制'}
                  </span>
                </InfoItem>
                <InfoItem label="展示工具调用详情">
                  <span
                    className={
                      cp?.showToolCalls
                        ? 'text-emerald-700 dark:text-emerald-500'
                        : 'text-muted-foreground'
                    }
                  >
                    {cp?.showToolCalls ? '是' : '否'}
                  </span>
                </InfoItem>
              </>
            )}
            <InfoItem label="调用者 ID 字段">
              <span className="text-sm">{cp?.callerUserIdField || '默认（渠道用户 ID）'}</span>
            </InfoItem>
            <InfoItem label="未命中斜杠指令透传">
              <span
                className={
                  cp?.unknownCommandAsAgentInput
                    ? 'text-emerald-700 dark:text-emerald-500'
                    : 'text-muted-foreground'
                }
              >
                {cp?.unknownCommandAsAgentInput ? '是' : '否'}
              </span>
            </InfoItem>
          </div>
        </CardContent>
      </Card>

      <Card>
        <CardHeader className="pb-3">
          <CardTitle className="text-base flex items-center gap-2">
            <svg
              className="w-4 h-4"
              fill="none"
              viewBox="0 0 24 24"
              stroke="currentColor"
              strokeWidth={2}
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z"
              />
            </svg>
            时间信息
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            <InfoItem label="创建时间">
              <span className="text-sm tabular-nums">{formatTimestamp(account.createdAt)}</span>
            </InfoItem>
            <InfoItem label="更新时间">
              <span className="text-sm tabular-nums">{formatTimestamp(account.updatedAt)}</span>
            </InfoItem>
            <InfoItem label="最后离线">
              <span className="text-sm tabular-nums">{formatTimestamp(account.lastOfflineAt)}</span>
            </InfoItem>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}

// ─── Sessions Tab ──────────────────────────────────────────────

const UUID_PATTERN = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;

function randomUUID(): string {
  if (typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function') {
    return crypto.randomUUID();
  }
  return 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, (c) => {
    const r = (Math.random() * 16) | 0;
    const v = c === 'x' ? r : (r & 0x3) | 0x8;
    return v.toString(16);
  });
}

type P2PRow = { localKey: string; id: string; name: string; idEditable: boolean };

function SessionsTab({ accountId }: { accountId: string }) {
  const queryClient = useQueryClient();
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const [userKeyword, setUserKeyword] = useState('');
  const [deleteId, setDeleteId] = useState<string | null>(null);
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [editId, setEditId] = useState<string | null>(null);
  const [p2pRows, setP2pRows] = useState<P2PRow[]>([]);
  const [currentIdx, setCurrentIdx] = useState(0);

  const listParams = useMemo(
    () =>
      create(ListRequestSchema, {
        page,
        pageSize,
        filter: { account_id: accountId },
        fuzzy: userKeyword.trim() ? { channel_user_id: userKeyword.trim() } : {},
      }),
    [page, pageSize, accountId, userKeyword]
  );

  const { data, isLoading } = useQuery({
    queryKey: ['channel_sessions', page, pageSize, accountId, userKeyword] as const,
    queryFn: () => channelApi.listChannelSessions(listParams),
  });

  const { data: editDetail, isLoading: editLoading } = useQuery({
    queryKey: ['channel_session', editId] as const,
    queryFn: () => channelApi.getChannelSession(editId!),
    enabled: drawerOpen && !!editId,
  });

  const resetDrawer = () => {
    setDrawerOpen(false);
    setEditId(null);
    setP2pRows([]);
    setCurrentIdx(0);
  };

  const updateMutation = useMutation({
    mutationFn: async () => {
      if (!editId || !editDetail) throw new Error('missing session');
      if (p2pRows.length === 0) throw new Error('至少保留一条会话');
      if (currentIdx < 0 || currentIdx >= p2pRows.length) {
        throw new Error('当前会话无效，请在列表中重新选择「设为当前」');
      }
      for (const r of p2pRows) {
        const sid = r.id.trim();
        if (!sid) throw new Error('请为新增会话填写会话 ID');
        if (!UUID_PATTERN.test(sid)) throw new Error(`会话 ID 须为 UUID：${sid}`);
      }
      const state = create(ChannelSessionStateSchema, {
        p2p: p2pRows.map((r) =>
          create(ChannelP2PSessionSchema, { id: r.id.trim(), name: r.name.trim() || '未命名' })
        ),
        currentIndex: currentIdx,
      });
      const body = create(ChannelSessionSchema, {
        id: editDetail.id,
        tenantId: editDetail.tenantId,
        accountId: editDetail.accountId,
        channelUserId: editDetail.channelUserId,
        state,
        createdAt: editDetail.createdAt,
        updatedAt: editDetail.updatedAt,
        deletedAt: editDetail.deletedAt,
      });
      await channelApi.updateChannelSession(editId, body);
      return editId;
    },
    onSuccess: (savedId) => {
      queryClient.invalidateQueries({ queryKey: ['channel_sessions'] });
      queryClient.removeQueries({ queryKey: ['channel_session', savedId] });
      resetDrawer();
    },
  });

  const deleteMutation = useMutation({
    mutationFn: (id: string) => channelApi.deleteChannelSession(id),
    onSuccess: () => {
      setDeleteId(null);
      queryClient.invalidateQueries({ queryKey: ['channel_sessions'] });
    },
  });

  useEffect(() => {
    if (!drawerOpen || !editDetail) return;
    const p2p = editDetail.state?.p2p ?? [];
    setP2pRows(
      p2p.map((s, i) => ({
        localKey: `loaded-${i}-${s.id || randomUUID()}`,
        id: s.id,
        name: s.name,
        idEditable: false,
      }))
    );
    setCurrentIdx(
      Math.min(Math.max(0, editDetail.state?.currentIndex ?? 0), Math.max(0, p2p.length - 1))
    );
  }, [drawerOpen, editDetail]);

  const rows = (data?.data ?? []).filter(Boolean);
  const totalCount = data?.total ?? 0;
  const totalPages = Math.max(1, Math.ceil(totalCount / pageSize));

  return (
    <div className="space-y-4">
      <div className="flex items-center gap-2">
        <Input
          placeholder="渠道用户 ID 模糊搜索…"
          value={userKeyword}
          onChange={(e) => {
            setUserKeyword(e.target.value);
            setPage(1);
          }}
          className="h-9 w-56"
        />
      </div>

      {isLoading ? (
        <div className="overflow-hidden rounded-xl border border-border/60 bg-card p-4 space-y-3">
          {[1, 2, 3].map((i) => (
            <Skeleton key={i} className="h-10 w-full" />
          ))}
        </div>
      ) : rows.length === 0 ? (
        <EmptyState title="暂无会话记录" description="网关处理消息时会自动创建会话" />
      ) : (
        <div className="overflow-x-auto rounded-xl border border-border/60 bg-card shadow-sm">
          <Table className="min-w-[780px]">
            <TableHeader>
              <TableRow className="hover:bg-transparent border-b bg-muted/40">
                <TableHead className="text-xs sm:text-sm">渠道用户</TableHead>
                <TableHead className="text-xs sm:text-sm">会话数</TableHead>
                <TableHead className="text-xs sm:text-sm">当前会话</TableHead>
                <TableHead className="text-xs sm:text-sm">更新时间</TableHead>
                <TableHead className="text-right text-xs sm:text-sm w-[7rem]">操作</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {rows.map((row) => {
                const p2p = row.state?.p2p ?? [];
                const p2pLen = p2p.length;
                const ci = row.state?.currentIndex;
                const idx = ci == null ? 0 : Number(ci);
                const current =
                  p2pLen > 0 && Number.isFinite(idx) && idx >= 0 && idx < p2pLen ? p2p[idx] : null;
                return (
                  <TableRow key={row.id} className="hover:bg-muted/50">
                    <TableCell
                      className="font-mono text-xs max-w-[10rem] truncate"
                      title={row.channelUserId}
                    >
                      {row.channelUserId || '—'}
                    </TableCell>
                    <TableCell className="tabular-nums">{p2pLen}</TableCell>
                    <TableCell className="max-w-[14rem]">
                      {current ? (
                        <div
                          className="flex min-w-0 flex-col gap-0.5 text-xs"
                          title={`${current.name}\n${current.id}`}
                        >
                          <span className="truncate font-medium text-foreground">
                            {current.name || '—'}
                          </span>
                          <span className="truncate font-mono text-muted-foreground">
                            {current.id || '—'}
                          </span>
                        </div>
                      ) : (
                        <span className="text-xs text-muted-foreground">—</span>
                      )}
                    </TableCell>
                    <TableCell className="text-muted-foreground text-xs whitespace-nowrap">
                      {formatTimestamp(row.updatedAt)}
                    </TableCell>
                    <TableCell className="text-right">
                      <div className="inline-flex items-center gap-1 justify-end">
                        <Button
                          variant="ghost"
                          size="icon"
                          className="h-8 w-8"
                          title="编辑状态"
                          onClick={() => {
                            setEditId(row.id);
                            setDrawerOpen(true);
                          }}
                        >
                          <PencilIcon className="h-4 w-4" />
                        </Button>
                        <Button
                          variant="ghost"
                          size="icon"
                          className="h-8 w-8 text-destructive"
                          title="删除"
                          onClick={() => setDeleteId(row.id)}
                        >
                          <TrashIcon className="h-4 w-4" />
                        </Button>
                      </div>
                    </TableCell>
                  </TableRow>
                );
              })}
              <TableRow>
                <TableCell colSpan={5} className="bg-muted/30">
                  <div className="flex items-center justify-between py-1">
                    <span className="text-sm text-muted-foreground">
                      共 {totalCount} 条，第 {page} / {totalPages} 页
                    </span>
                    <div className="flex items-center gap-3">
                      <div className="flex items-center gap-1.5">
                        <span className="text-sm text-muted-foreground">每页</span>
                        <select
                          value={pageSize}
                          onChange={(e) => {
                            setPageSize(Number(e.target.value));
                            setPage(1);
                          }}
                          className="h-7 rounded-md border border-input bg-background px-2 text-sm"
                        >
                          <option value={10}>10</option>
                          <option value={20}>20</option>
                          <option value={50}>50</option>
                        </select>
                      </div>
                      <div className="flex items-center gap-1">
                        <Button
                          variant="outline"
                          size="sm"
                          className="h-7 px-2"
                          disabled={page <= 1}
                          onClick={() => setPage((p) => Math.max(1, p - 1))}
                        >
                          上一页
                        </Button>
                        <Button
                          variant="outline"
                          size="sm"
                          className="h-7 px-2"
                          disabled={page >= totalPages}
                          onClick={() => setPage((p) => p + 1)}
                        >
                          下一页
                        </Button>
                      </div>
                    </div>
                  </div>
                </TableCell>
              </TableRow>
            </TableBody>
          </Table>
        </div>
      )}

      <SlideOver
        open={drawerOpen}
        onOpenChange={(open) => {
          if (!open) resetDrawer();
        }}
        title="编辑渠道会话状态"
        actions={
          <div className="flex items-center gap-2">
            <Button variant="outline" size="sm" onClick={() => resetDrawer()}>
              取消
            </Button>
            <Button
              size="sm"
              disabled={
                editLoading || !editDetail || p2pRows.length === 0 || updateMutation.isPending
              }
              onClick={() => updateMutation.mutate()}
            >
              {updateMutation.isPending && <LoaderIcon className="mr-2 h-4 w-4 animate-spin" />}
              保存
            </Button>
          </div>
        }
      >
        {editLoading || !editDetail ? (
          <div className="flex items-center gap-2 py-12 text-sm text-muted-foreground">
            <LoaderIcon className="h-5 w-5 animate-spin" />
            加载中…
          </div>
        ) : (
          <div className="space-y-4 max-w-lg">
            <div className="space-y-1 text-sm">
              <p>
                <span className="text-muted-foreground">会话 ID：</span>
                <span className="font-mono text-xs break-all">{editDetail.id}</span>
              </p>
              <p>
                <span className="text-muted-foreground">渠道用户：</span>
                <span className="font-mono text-xs break-all">{editDetail.channelUserId}</span>
              </p>
            </div>
            <div className="space-y-2">
              <div className="flex items-center justify-between gap-2">
                <Label>会话列表</Label>
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  className="h-8 gap-1"
                  onClick={() => {
                    setP2pRows((prev) => {
                      const next = [
                        ...prev,
                        { localKey: randomUUID(), id: '', name: '', idEditable: true },
                      ];
                      setCurrentIdx(prev.length);
                      return next;
                    });
                  }}
                >
                  <PlusIcon className="h-3.5 w-3.5" />
                  添加会话
                </Button>
              </div>
              <div className="rounded-md border max-h-72 overflow-y-auto divide-y divide-border">
                {p2pRows.map((r, idx) => (
                  <div
                    key={r.localKey}
                    className={cn(
                      'flex flex-col gap-2 p-3 bg-background sm:flex-row sm:flex-wrap sm:items-center',
                      idx === currentIdx && 'bg-primary/[0.06] ring-1 ring-inset ring-primary/25'
                    )}
                  >
                    <div className="flex shrink-0 items-center gap-2 min-w-0 sm:w-[7rem]">
                      {idx === currentIdx ? (
                        <span className="text-xs font-semibold text-primary">当前使用</span>
                      ) : (
                        <Button
                          type="button"
                          variant="outline"
                          size="sm"
                          className="h-7 px-2 text-xs"
                          onClick={() => setCurrentIdx(idx)}
                        >
                          设为当前
                        </Button>
                      )}
                    </div>
                    <div className="flex min-w-0 flex-1 flex-col gap-2 sm:flex-row sm:items-center sm:gap-2">
                      <div className="flex min-w-0 flex-1 items-center gap-1.5">
                        {r.idEditable ? (
                          <>
                            <Input
                              className="h-8 min-w-0 font-mono text-xs"
                              value={r.id}
                              onChange={(e) => {
                                const v = e.target.value;
                                setP2pRows((prev) =>
                                  prev.map((x, i) => (i === idx ? { ...x, id: v } : x))
                                );
                              }}
                              placeholder="会话 ID（UUID）"
                              spellCheck={false}
                            />
                            <Button
                              type="button"
                              variant="secondary"
                              size="icon"
                              className="h-8 w-8 shrink-0"
                              title="随机生成 UUID"
                              onClick={() => {
                                const uid = randomUUID();
                                setP2pRows((prev) =>
                                  prev.map((x, i) => (i === idx ? { ...x, id: uid } : x))
                                );
                              }}
                            >
                              <RegenerateUuidIcon className="h-4 w-4" />
                            </Button>
                          </>
                        ) : (
                          <div
                            className="flex h-8 min-w-0 flex-1 items-center rounded-md border border-input bg-muted/30 px-3 font-mono text-xs text-foreground"
                            title={r.id}
                          >
                            <span className="truncate">{r.id || '—'}</span>
                          </div>
                        )}
                      </div>
                      <Input
                        className="h-8 min-w-[8rem] flex-1 text-sm"
                        value={r.name}
                        onChange={(e) => {
                          const v = e.target.value;
                          setP2pRows((prev) =>
                            prev.map((x, i) => (i === idx ? { ...x, name: v } : x))
                          );
                        }}
                        placeholder="显示名称"
                      />
                      <Button
                        type="button"
                        variant="ghost"
                        size="icon"
                        className="h-8 w-8 shrink-0 text-destructive sm:ml-auto"
                        disabled={p2pRows.length <= 1}
                        onClick={() => {
                          setP2pRows((prev) => {
                            if (prev.length <= 1) return prev;
                            const next = prev.filter((_, i) => i !== idx);
                            setCurrentIdx((ci) => {
                              let ni = ci;
                              if (idx === ci) ni = Math.min(ci, next.length - 1);
                              else if (idx < ci) ni = ci - 1;
                              return Math.max(0, Math.min(ni, next.length - 1));
                            });
                            return next;
                          });
                        }}
                        title="删除会话"
                      >
                        <TrashIcon className="h-3.5 w-3.5" />
                      </Button>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        )}
      </SlideOver>

      <AlertDialog open={!!deleteId} onOpenChange={() => setDeleteId(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>确认删除会话记录？</AlertDialogTitle>
            <AlertDialogDescription>
              将软删除该条会话并清除对应缓存；网关会按缺失会话重新创建默认状态。
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>取消</AlertDialogCancel>
            <AlertDialogAction
              className="bg-destructive hover:bg-destructive/90"
              onClick={() => deleteId && deleteMutation.mutate(deleteId)}
            >
              {deleteMutation.isPending && <LoaderIcon className="mr-2 h-4 w-4 animate-spin" />}
              删除
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}

// ─── User Bindings Tab ────────────────────────────────────────

function UserBindingsTab({ accountId }: { accountId: string }) {
  const queryClient = useQueryClient();
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const [deleteId, setDeleteId] = useState<string | null>(null);
  const [editingBindingId, setEditingBindingId] = useState<string | null>(null);
  const [editingAllowRules, setEditingAllowRules] = useState<
    { toolName: string; argument: string; pattern: string }[]
  >([]);
  const [editingDenyRules, setEditingDenyRules] = useState<
    { toolName: string; argument: string; pattern: string }[]
  >([]);

  const listParams = useMemo(
    () =>
      create(ListRequestSchema, {
        page,
        pageSize,
        filter: { account_id: accountId },
      }),
    [page, pageSize, accountId]
  );

  const { data, isLoading } = useQuery({
    queryKey: ['channel_user_bindings', page, pageSize, accountId] as const,
    queryFn: () => channelApi.listChannelUserBindings(listParams),
  });

  const deleteMutation = useMutation({
    mutationFn: (id: string) => channelApi.deleteChannelUserBinding(id),
    onSuccess: () => {
      setDeleteId(null);
      queryClient.invalidateQueries({ queryKey: ['channel_user_bindings'] });
    },
  });

  const updateMutation = useMutation({
    mutationFn: ({ id, data }: { id: string; data: ChannelUserBinding }) =>
      channelApi.updateChannelUserBinding(id, data),
    onSuccess: () => {
      setEditingBindingId(null);
      queryClient.invalidateQueries({ queryKey: ['channel_user_bindings'] });
    },
  });

  const rows = (data?.data ?? []).filter(Boolean);
  const totalCount = data?.total ?? 0;
  const totalPages = Math.max(1, Math.ceil(totalCount / pageSize));

  return (
    <div className="space-y-4">
      {isLoading ? (
        <div className="overflow-hidden rounded-xl border border-border/60 bg-card p-4 space-y-3">
          {[1, 2, 3].map((i) => (
            <Skeleton key={i} className="h-10 w-full" />
          ))}
        </div>
      ) : rows.length === 0 ? (
        <EmptyState title="暂无用户绑定" description="终端用户执行 /init 时会自动创建绑定记录" />
      ) : (
        <div className="overflow-x-auto rounded-xl border border-border/60 bg-card shadow-sm">
          <Table className="min-w-[780px]">
            <TableHeader>
              <TableRow className="hover:bg-transparent border-b bg-muted/40">
                <TableHead className="text-xs sm:text-sm">渠道用户 ID</TableHead>
                <TableHead className="text-xs sm:text-sm">用户名</TableHead>
                <TableHead className="text-xs sm:text-sm">Open ID</TableHead>
                <TableHead className="text-xs sm:text-sm">绑定应用</TableHead>
                <TableHead className="text-xs sm:text-sm">更新时间</TableHead>
                <TableHead className="text-right text-xs sm:text-sm w-[7rem]">操作</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {rows.map((row) => (
                <TableRow key={row.id} className="hover:bg-muted/50">
                  <TableCell
                    className="font-mono text-xs max-w-[10rem] truncate"
                    title={row.channelUserId}
                  >
                    {row.channelUserId || '—'}
                  </TableCell>
                  <TableCell className="text-sm">{row.channelUser?.username || '—'}</TableCell>
                  <TableCell
                    className="font-mono text-xs max-w-[10rem] truncate"
                    title={row.channelUser?.openId}
                  >
                    {row.channelUser?.openId || '—'}
                  </TableCell>
                  <TableCell
                    className="font-mono text-xs max-w-[10rem] truncate"
                    title={row.applicationId}
                  >
                    {row.applicationId || '—'}
                  </TableCell>
                  <TableCell className="text-muted-foreground text-xs whitespace-nowrap">
                    {formatTimestamp(row.updatedAt)}
                  </TableCell>
                  <TableCell className="text-right">
                    <div className="inline-flex items-center gap-1 justify-end">
                      <Button
                        variant="ghost"
                        size="icon"
                        className="h-8 w-8 text-primary"
                        title="编辑审批规则"
                        onClick={() => {
                          setEditingBindingId(row.id);
                          setEditingAllowRules(
                            (row.toolPermissions?.allow ?? []).map((r) => ({
                              toolName: r.toolName,
                              argument: r.argument,
                              pattern: r.pattern,
                            }))
                          );
                          setEditingDenyRules(
                            (row.toolPermissions?.deny ?? []).map((r) => ({
                              toolName: r.toolName,
                              argument: r.argument,
                              pattern: r.pattern,
                            }))
                          );
                        }}
                      >
                        <PencilIcon className="h-4 w-4" />
                      </Button>
                      <Button
                        variant="ghost"
                        size="icon"
                        className="h-8 w-8 text-destructive"
                        title="删除绑定"
                        onClick={() => setDeleteId(row.id)}
                      >
                        <TrashIcon className="h-4 w-4" />
                      </Button>
                    </div>
                  </TableCell>
                </TableRow>
              ))}
              <TableRow>
                <TableCell colSpan={6} className="bg-muted/30">
                  <div className="flex items-center justify-between py-1">
                    <span className="text-sm text-muted-foreground">
                      共 {totalCount} 条，第 {page} / {totalPages} 页
                    </span>
                    <div className="flex items-center gap-3">
                      <div className="flex items-center gap-1.5">
                        <span className="text-sm text-muted-foreground">每页</span>
                        <select
                          value={pageSize}
                          onChange={(e) => {
                            setPageSize(Number(e.target.value));
                            setPage(1);
                          }}
                          className="h-7 rounded-md border border-input bg-background px-2 text-sm"
                        >
                          <option value={10}>10</option>
                          <option value={20}>20</option>
                          <option value={50}>50</option>
                        </select>
                      </div>
                      <div className="flex items-center gap-1">
                        <Button
                          variant="outline"
                          size="sm"
                          className="h-7 px-2"
                          disabled={page <= 1}
                          onClick={() => setPage((p) => Math.max(1, p - 1))}
                        >
                          上一页
                        </Button>
                        <Button
                          variant="outline"
                          size="sm"
                          className="h-7 px-2"
                          disabled={page >= totalPages}
                          onClick={() => setPage((p) => p + 1)}
                        >
                          下一页
                        </Button>
                      </div>
                    </div>
                  </div>
                </TableCell>
              </TableRow>
            </TableBody>
          </Table>
        </div>
      )}

      <AlertDialog open={!!deleteId} onOpenChange={() => setDeleteId(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>确认删除用户绑定？</AlertDialogTitle>
            <AlertDialogDescription>
              删除后该用户下次发送消息时将重新触发 /init 流程创建新实例。
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>取消</AlertDialogCancel>
            <AlertDialogAction
              className="bg-destructive hover:bg-destructive/90"
              onClick={() => deleteId && deleteMutation.mutate(deleteId)}
            >
              {deleteMutation.isPending && <LoaderIcon className="mr-2 h-4 w-4 animate-spin" />}
              删除
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      <SlideOver
        open={!!editingBindingId}
        onOpenChange={(open) => {
          if (!open) setEditingBindingId(null);
        }}
        title="工具权限规则"
        description="deny 规则优先 → 匹配则直接拒绝；然后 allow 规则 → 匹配则自动通过审批；无匹配则需人工审批确认。"
      >
        <div className="space-y-6">
          {/* Deny rules */}
          <div className="space-y-3">
            <div className="flex items-center justify-between">
              <h4 className="text-sm font-medium text-destructive">拒绝规则 (deny)</h4>
              <Button
                type="button"
                variant="outline"
                size="sm"
                onClick={() =>
                  setEditingDenyRules([
                    ...editingDenyRules,
                    { toolName: '', argument: '', pattern: '' },
                  ])
                }
              >
                <PlusIcon className="h-3.5 w-3.5 mr-1" />
                添加拒绝规则
              </Button>
            </div>
            {editingDenyRules.length === 0 && (
              <div className="text-sm text-muted-foreground text-center py-4 border rounded-lg border-dashed">
                无拒绝规则
              </div>
            )}
            {editingDenyRules.map((rule, i) => (
              <div
                key={`deny-${i}`}
                className="rounded-lg border border-destructive/20 bg-destructive/5 p-4 space-y-3"
              >
                <div className="flex items-center gap-3">
                  <div className="flex-1 min-w-0 space-y-1.5">
                    <Label className="text-xs text-muted-foreground">工具名称</Label>
                    <Input
                      placeholder="exec、file_editor 或 * (所有工具)"
                      value={rule.toolName}
                      onChange={(e) => {
                        const next = [...editingDenyRules];
                        next[i] = { ...next[i], toolName: e.target.value };
                        setEditingDenyRules(next);
                      }}
                      className="h-9 font-mono text-xs"
                    />
                  </div>
                  <div className="flex-1 min-w-0 space-y-1.5">
                    <Label className="text-xs text-muted-foreground">参数名</Label>
                    <Input
                      placeholder="command、path 等（留空表示不按参数过滤）"
                      value={rule.argument}
                      onChange={(e) => {
                        const next = [...editingDenyRules];
                        next[i] = { ...next[i], argument: e.target.value };
                        setEditingDenyRules(next);
                      }}
                      className="h-9 font-mono text-xs"
                    />
                  </div>
                  <Button
                    type="button"
                    variant="ghost"
                    size="icon"
                    className="h-8 w-8 shrink-0 text-muted-foreground hover:text-destructive"
                    onClick={() => setEditingDenyRules(editingDenyRules.filter((_, j) => j !== i))}
                  >
                    <TrashIcon className="h-4 w-4" />
                  </Button>
                </div>
                <div className="space-y-1.5">
                  <Label className="text-xs text-muted-foreground">匹配正则 (RE2 语法)</Label>
                  <Input
                    placeholder="如 ^rm.* （留空表示无条件拒绝该工具）"
                    value={rule.pattern}
                    onChange={(e) => {
                      const next = [...editingDenyRules];
                      next[i] = { ...next[i], pattern: e.target.value };
                      setEditingDenyRules(next);
                    }}
                    className="h-9 font-mono text-xs"
                  />
                </div>
              </div>
            ))}
          </div>

          {/* Allow rules */}
          <div className="space-y-3">
            <div className="flex items-center justify-between">
              <h4 className="text-sm font-medium text-green-600">允许规则 (allow)</h4>
              <Button
                type="button"
                variant="outline"
                size="sm"
                onClick={() =>
                  setEditingAllowRules([
                    ...editingAllowRules,
                    { toolName: '', argument: '', pattern: '' },
                  ])
                }
              >
                <PlusIcon className="h-3.5 w-3.5 mr-1" />
                添加允许规则
              </Button>
            </div>
            {editingAllowRules.length === 0 && (
              <div className="text-sm text-muted-foreground text-center py-4 border rounded-lg border-dashed">
                无允许规则，所有工具调用需人工审批
              </div>
            )}
            {editingAllowRules.map((rule, i) => (
              <div
                key={`allow-${i}`}
                className="rounded-lg border border-green-200 bg-green-50 p-4 space-y-3"
              >
                <div className="flex items-center gap-3">
                  <div className="flex-1 min-w-0 space-y-1.5">
                    <Label className="text-xs text-muted-foreground">工具名称</Label>
                    <Input
                      placeholder="exec、file_editor 或 * (所有工具)"
                      value={rule.toolName}
                      onChange={(e) => {
                        const next = [...editingAllowRules];
                        next[i] = { ...next[i], toolName: e.target.value };
                        setEditingAllowRules(next);
                      }}
                      className="h-9 font-mono text-xs"
                    />
                  </div>
                  <div className="flex-1 min-w-0 space-y-1.5">
                    <Label className="text-xs text-muted-foreground">参数名</Label>
                    <Input
                      placeholder="command、path 等（留空表示不按参数过滤）"
                      value={rule.argument}
                      onChange={(e) => {
                        const next = [...editingAllowRules];
                        next[i] = { ...next[i], argument: e.target.value };
                        setEditingAllowRules(next);
                      }}
                      className="h-9 font-mono text-xs"
                    />
                  </div>
                  <Button
                    type="button"
                    variant="ghost"
                    size="icon"
                    className="h-8 w-8 shrink-0 text-muted-foreground hover:text-destructive"
                    onClick={() =>
                      setEditingAllowRules(editingAllowRules.filter((_, j) => j !== i))
                    }
                  >
                    <TrashIcon className="h-4 w-4" />
                  </Button>
                </div>
                <div className="space-y-1.5">
                  <Label className="text-xs text-muted-foreground">匹配正则 (RE2 语法)</Label>
                  <Input
                    placeholder="如 ^ls.* 或 ^cat |^grep （留空表示无条件允许该工具）"
                    value={rule.pattern}
                    onChange={(e) => {
                      const next = [...editingAllowRules];
                      next[i] = { ...next[i], pattern: e.target.value };
                      setEditingAllowRules(next);
                    }}
                    className="h-9 font-mono text-xs"
                  />
                </div>
              </div>
            ))}
          </div>

          <div className="flex justify-end pt-4 border-t">
            <Button
              onClick={() => {
                if (!editingBindingId) return;
                const binding = create(ChannelUserBindingSchema, {
                  id: editingBindingId,
                  toolPermissions: create(ToolPermissionsSchema, {
                    allow: editingAllowRules.map((r) =>
                      create(ToolPermissionRuleSchema, {
                        toolName: r.toolName,
                        argument: r.argument,
                        pattern: r.pattern,
                      })
                    ),
                    deny: editingDenyRules.map((r) =>
                      create(ToolPermissionRuleSchema, {
                        toolName: r.toolName,
                        argument: r.argument,
                        pattern: r.pattern,
                      })
                    ),
                  }),
                });
                updateMutation.mutate({ id: editingBindingId, data: binding });
              }}
              disabled={updateMutation.isPending}
            >
              {updateMutation.isPending && <LoaderIcon className="mr-2 h-4 w-4 animate-spin" />}
              保存
            </Button>
          </div>
        </div>
      </SlideOver>
    </div>
  );
}

// ─── Cron Jobs Tab ───────────────────────────────────────────────

function JobStatusBadge({ status }: { status: JobStatus }) {
  switch (status) {
    case JobStatus.RUNNING:
      return (
        <Badge variant="outline" className="text-blue-600 border-blue-300">
          运行中
        </Badge>
      );
    case JobStatus.COMPLETED:
      return (
        <Badge variant="outline" className="text-green-600 border-green-300">
          已完成
        </Badge>
      );
    case JobStatus.PAUSED:
      return (
        <Badge variant="outline" className="text-gray-600 border-gray-300">
          已暂停
        </Badge>
      );
    default:
      return <Badge variant="outline">未知</Badge>;
  }
}

function JobRunStatusBadge({ status }: { status: JobRunStatus }) {
  switch (status) {
    case JobRunStatus.RUNNING:
      return (
        <Badge variant="outline" className="text-blue-600 border-blue-300">
          运行中
        </Badge>
      );
    case JobRunStatus.COMPLETED:
      return (
        <Badge variant="outline" className="text-green-600 border-green-300">
          已完成
        </Badge>
      );
    case JobRunStatus.FAILED:
      return <Badge variant="destructive">失败</Badge>;
    default:
      return <Badge variant="outline">未知</Badge>;
  }
}

function formatDuration(ms: number | bigint | undefined) {
  if (ms == null || ms === 0n) return '-';
  const n = Number(ms);
  if (!n) return '-';
  if (n < 1000) return `${n}ms`;
  if (n < 60000) return `${(n / 1000).toFixed(1)}s`;
  return `${Math.floor(n / 60000)}m${Math.floor((n % 60000) / 1000)}s`;
}

function JobRunDetailSlideOver({
  jobId,
  runId,
  open,
  onOpenChange,
}: {
  jobId: string;
  runId: string | null;
  open: boolean;
  onOpenChange: (open: boolean) => void;
}) {
  const { data: run, isLoading } = useQuery({
    queryKey: ['job_run_detail', runId] as const,
    queryFn: () => jobApi.getJobRun({ jobId, runId: runId! }),
    enabled: open && !!runId,
  });
  const { data: job } = useQuery({
    queryKey: ['job_detail', jobId] as const,
    queryFn: () => jobApi.getJob(jobId),
    enabled: open && !!runId,
  });
  const agentInput = job?.jobSpec?.case === 'agentic' ? job.jobSpec.value.agentInput : undefined;

  return (
    <SlideOver open={open} onOpenChange={onOpenChange} title="运行详情" description={runId ?? ''}>
      {isLoading || !run ? (
        <div className="space-y-3">
          {[1, 2, 3].map((i) => (
            <Skeleton key={i} className="h-8 w-full" />
          ))}
        </div>
      ) : (
        <div className="space-y-4">
          <div className="grid grid-cols-2 gap-3">
            <div>
              <p className="text-xs text-muted-foreground">状态</p>
              <JobRunStatusBadge status={run.status} />
            </div>
            <div>
              <p className="text-xs text-muted-foreground">耗时</p>
              <p className="text-sm">{formatDuration(run.durationMs)}</p>
            </div>
            <div>
              <p className="text-xs text-muted-foreground">开始时间</p>
              <p className="text-sm">{formatTimestamp(run.startedAt)}</p>
            </div>
            <div>
              <p className="text-xs text-muted-foreground">结束时间</p>
              <p className="text-sm">{formatTimestamp(run.finishedAt)}</p>
            </div>
          </div>

          {agentInput && (
            <div>
              <p className="text-xs text-muted-foreground mb-1">Agent 输入</p>
              <pre className="text-sm bg-muted/50 rounded-lg p-3 whitespace-pre-wrap break-all max-h-40 overflow-y-auto">
                {agentInput}
              </pre>
            </div>
          )}

          {run.result && (
            <div>
              <p className="text-xs text-muted-foreground mb-1">执行结果</p>
              <pre className="text-sm bg-muted/50 rounded-lg p-3 whitespace-pre-wrap break-all max-h-60 overflow-y-auto">
                {run.result}
              </pre>
            </div>
          )}

          {run.errorMessage && (
            <div>
              <p className="text-xs text-muted-foreground mb-1">错误信息</p>
              <pre className="text-sm bg-destructive/10 text-destructive rounded-lg p-3 whitespace-pre-wrap break-all max-h-40 overflow-y-auto">
                {run.errorMessage}
              </pre>
            </div>
          )}
        </div>
      )}
    </SlideOver>
  );
}

function JobRunsSlideOver({
  jobId,
  jobName,
  open,
  onOpenChange,
}: {
  jobId: string;
  jobName: string;
  open: boolean;
  onOpenChange: (open: boolean) => void;
}) {
  const [page, setPage] = useState(1);
  const pageSize = 10;
  const [detailRunId, setDetailRunId] = useState<string | null>(null);

  const { data, isLoading } = useQuery({
    queryKey: ['job_runs', jobId, page, pageSize] as const,
    queryFn: () =>
      jobApi.listJobRuns(
        create(ListRequestSchema, {
          page,
          pageSize,
          filter: { job_id: jobId },
        })
      ),
    enabled: open,
  });

  const runs = data?.data ?? [];
  const totalCount = data?.total ?? 0;
  const totalPages = Math.max(1, Math.ceil(totalCount / pageSize));

  return (
    <>
      <SlideOver
        open={open}
        onOpenChange={onOpenChange}
        title={`运行记录 — ${jobName}`}
        description={`Job ID: ${jobId}`}
      >
        {isLoading ? (
          <div className="space-y-3">
            {[1, 2, 3].map((i) => (
              <Skeleton key={i} className="h-10 w-full" />
            ))}
          </div>
        ) : runs.length === 0 ? (
          <EmptyState title="暂无运行记录" description="该定时任务尚未触发过" />
        ) : (
          <div className="space-y-4">
            <div className="overflow-x-auto rounded-xl border border-border/60 bg-card shadow-sm">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>开始时间</TableHead>
                    <TableHead>状态</TableHead>
                    <TableHead>耗时</TableHead>
                    <TableHead>结果</TableHead>
                    <TableHead>错误信息</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {runs.map((run) => (
                    <TableRow
                      key={run.id}
                      className="cursor-pointer hover:bg-muted/50"
                      onClick={() => setDetailRunId(run.id)}
                    >
                      <TableCell className="whitespace-nowrap text-sm">
                        {formatTimestamp(run.startedAt)}
                      </TableCell>
                      <TableCell>
                        <JobRunStatusBadge status={run.status} />
                      </TableCell>
                      <TableCell className="whitespace-nowrap text-sm">
                        {formatDuration(run.durationMs)}
                      </TableCell>
                      <TableCell className="max-w-[200px] truncate text-sm text-muted-foreground">
                        {run.result || '-'}
                      </TableCell>
                      <TableCell className="max-w-[200px] truncate text-sm text-destructive">
                        {run.errorMessage || '-'}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>

            {totalCount > pageSize && (
              <div className="flex items-center justify-between">
                <span className="text-sm text-muted-foreground">
                  共 {totalCount} 条，第 {page}/{totalPages} 页
                </span>
                <div className="flex gap-2">
                  <Button
                    size="sm"
                    variant="outline"
                    disabled={page <= 1}
                    onClick={() => setPage(page - 1)}
                  >
                    上一页
                  </Button>
                  <Button
                    size="sm"
                    variant="outline"
                    disabled={page >= totalPages}
                    onClick={() => setPage(page + 1)}
                  >
                    下一页
                  </Button>
                </div>
              </div>
            )}
          </div>
        )}
      </SlideOver>

      {detailRunId && (
        <JobRunDetailSlideOver
          jobId={jobId}
          runId={detailRunId}
          open={!!detailRunId}
          onOpenChange={(open) => {
            if (!open) setDetailRunId(null);
          }}
        />
      )}
    </>
  );
}

function CronJobsTab({ accountId }: { accountId: string }) {
  const queryClient = useQueryClient();
  const [page, setPage] = useState(1);
  const pageSize = 10;
  const [selectedJobId, setSelectedJobId] = useState<string | null>(null);
  const [selectedJobName, setSelectedJobName] = useState<string>('');
  const [deleteId, setDeleteId] = useState<string | null>(null);

  const { data, isLoading } = useQuery({
    queryKey: ['cron_jobs', page, pageSize, accountId] as const,
    queryFn: () =>
      jobApi.listJobs(
        create(ListRequestSchema, {
          page,
          pageSize,
          filter: { type: String(JobType.AGENTIC) },
        })
      ),
  });

  const deleteMutation = useMutation({
    mutationFn: (id: string) => jobApi.deleteJob(id),
    onSuccess: () => {
      setDeleteId(null);
      queryClient.invalidateQueries({ queryKey: ['cron_jobs'] });
    },
  });

  const pauseMutation = useMutation({
    mutationFn: (id: string) => jobApi.pauseJob(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['cron_jobs'] });
    },
  });

  const resumeMutation = useMutation({
    mutationFn: (id: string) => jobApi.resumeJob(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['cron_jobs'] });
    },
  });

  const rows = data?.data ?? [];
  const totalCount = data?.total ?? 0;
  const totalPages = Math.max(1, Math.ceil(totalCount / pageSize));

  return (
    <div className="space-y-4">
      {isLoading ? (
        <div className="overflow-hidden rounded-xl border border-border/60 bg-card p-4 space-y-3">
          {[1, 2, 3].map((i) => (
            <Skeleton key={i} className="h-10 w-full" />
          ))}
        </div>
      ) : rows.length === 0 ? (
        <EmptyState title="暂无定时任务" description="通过 /cron add 命令创建定时任务" />
      ) : (
        <div className="overflow-x-auto rounded-xl border border-border/60 bg-card shadow-sm">
          <Table className="min-w-[780px]">
            <TableHeader>
              <TableRow>
                <TableHead>名称</TableHead>
                <TableHead>Cron 表达式</TableHead>
                <TableHead>状态</TableHead>
                <TableHead className="text-right">操作</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {rows.map((job) => (
                <TableRow key={job.id}>
                  <TableCell className="font-medium">{job.name || '-'}</TableCell>
                  <TableCell className="whitespace-nowrap text-sm text-muted-foreground">
                    {job.schedule?.case === 'cron' ? job.schedule.value : '-'}
                  </TableCell>
                  <TableCell>
                    <JobStatusBadge status={job.status} />
                  </TableCell>
                  <TableCell className="whitespace-nowrap text-sm">
                    {job.schedule?.case === 'runAt' ? formatTimestamp(job.schedule.value) : '-'}
                  </TableCell>
                  <TableCell className="text-right">
                    <div className="flex items-center justify-end gap-1">
                      {job.status === JobStatus.RUNNING && (
                        <Button
                          size="icon"
                          variant="ghost"
                          className="h-8 w-8"
                          title="暂停"
                          disabled={pauseMutation.isPending}
                          onClick={() => pauseMutation.mutate(job.id)}
                        >
                          <PauseIcon className="h-4 w-4" />
                        </Button>
                      )}
                      {job.status === JobStatus.PAUSED && (
                        <Button
                          size="icon"
                          variant="ghost"
                          className="h-8 w-8"
                          title="恢复"
                          disabled={resumeMutation.isPending}
                          onClick={() => resumeMutation.mutate(job.id)}
                        >
                          <PlayIcon className="h-4 w-4" />
                        </Button>
                      )}
                      <Button
                        size="icon"
                        variant="ghost"
                        className="h-8 w-8"
                        title="运行记录"
                        onClick={() => {
                          setSelectedJobId(job.id);
                          setSelectedJobName(job.name || '');
                        }}
                      >
                        <EyeIcon className="h-4 w-4" />
                      </Button>
                      <Button
                        size="icon"
                        variant="ghost"
                        className="h-8 w-8 text-destructive"
                        title="删除"
                        onClick={() => setDeleteId(job.id)}
                      >
                        <TrashIcon className="h-4 w-4" />
                      </Button>
                    </div>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </div>
      )}

      {totalCount > pageSize && (
        <div className="flex items-center justify-between">
          <span className="text-sm text-muted-foreground">
            共 {totalCount} 条，第 {page}/{totalPages} 页
          </span>
          <div className="flex gap-2">
            <Button
              size="sm"
              variant="outline"
              disabled={page <= 1}
              onClick={() => setPage(page - 1)}
            >
              上一页
            </Button>
            <Button
              size="sm"
              variant="outline"
              disabled={page >= totalPages}
              onClick={() => setPage(page + 1)}
            >
              下一页
            </Button>
          </div>
        </div>
      )}

      {selectedJobId && (
        <JobRunsSlideOver
          jobId={selectedJobId}
          jobName={selectedJobName}
          open={!!selectedJobId}
          onOpenChange={(open) => {
            if (!open) setSelectedJobId(null);
          }}
        />
      )}

      <AlertDialog
        open={!!deleteId}
        onOpenChange={(open) => {
          if (!open) setDeleteId(null);
        }}
      >
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>确认删除</AlertDialogTitle>
            <AlertDialogDescription>将删除该定时任务，不可撤销。</AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>取消</AlertDialogCancel>
            <AlertDialogAction
              className="bg-destructive hover:bg-destructive/90"
              onClick={() => {
                if (deleteId) deleteMutation.mutate(deleteId);
              }}
            >
              {deleteMutation.isPending && <LoaderIcon className="mr-2 h-4 w-4 animate-spin" />}
              删除
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}

// ─── Main Detail Page ─────────────────────────────────────────

type TabId = 'overview' | 'sessions' | 'bindings' | 'jobs';

export default function ChannelAccountDetailPage() {
  const navigate = useNavigate();
  const { accountId } = useParams<{ accountId: string }>();
  const [activeTab, setActiveTab] = useState<TabId>('overview');

  // 返回列表时带上 ?type=<slug>，落回进入详情前所在的 tab。
  const [sp] = useSearchParams();
  const listTypeSlug = sp.get('type') ?? undefined;
  const backToListPath = listTypeSlug
    ? `/channel-accounts?type=${listTypeSlug}`
    : '/channel-accounts';

  const {
    data: account,
    isLoading,
    error,
  } = useQuery({
    queryKey: ['channel_account', accountId] as const,
    queryFn: () => channelApi.getChannelAccount(accountId!),
    enabled: !!accountId,
  });

  const queryClient = useQueryClient();

  const enableMutation = useMutation({
    mutationFn: () => channelApi.enableChannelAccount(accountId!),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['channel_account', accountId] });
      queryClient.invalidateQueries({ queryKey: ['channel_accounts'] });
    },
  });

  const disableMutation = useMutation({
    mutationFn: () => channelApi.disableChannelAccount(accountId!),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['channel_account', accountId] });
      queryClient.invalidateQueries({ queryKey: ['channel_accounts'] });
    },
  });

  const deleteMutation = useMutation({
    mutationFn: () => channelApi.deleteChannelAccount(accountId!),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['channel_accounts'] });
      toast.success('已删除');
      navigate(backToListPath);
    },
    onError: (e) => {
      if (e instanceof ApiError && e.reason === 'account_enabled') {
        toast.error('该渠道账号已启用，请先停用后再删除');
        return;
      }
      toast.error(e instanceof ApiError ? e.message : (e as Error).message);
    },
  });

  const [showDeleteDialog, setShowDeleteDialog] = useState(false);

  const tabs = [
    { id: 'overview' as TabId, label: '概览' },
    { id: 'sessions' as TabId, label: '会话管理' },
    { id: 'bindings' as TabId, label: '用户绑定' },
    { id: 'jobs' as TabId, label: '定时任务' },
  ];

  if (isLoading) {
    return (
      <div className="mx-auto max-w-[1440px] space-y-6">
        <div className="flex items-center gap-3">
          <Skeleton className="h-9 w-9" />
          <div className="space-y-2">
            <Skeleton className="h-7 w-48" />
            <Skeleton className="h-4 w-64" />
          </div>
        </div>
        <Skeleton className="h-10 w-64" />
        <div className="space-y-4">
          <Skeleton className="h-48 w-full" />
          <Skeleton className="h-36 w-full" />
        </div>
      </div>
    );
  }

  if (error || !account) {
    return (
      <div className="mx-auto max-w-[1440px] space-y-6">
        <Button variant="ghost" size="sm" onClick={() => navigate(backToListPath)}>
          <svg
            className="w-4 h-4 mr-1"
            fill="none"
            viewBox="0 0 24 24"
            stroke="currentColor"
            strokeWidth={2}
          >
            <path strokeLinecap="round" strokeLinejoin="round" d="M15 19l-7-7 7-7" />
          </svg>
          返回列表
        </Button>
        <div className="text-center py-20">
          <p className="text-muted-foreground">加载渠道账号详情失败</p>
        </div>
      </div>
    );
  }

  return (
    <div className="mx-auto max-w-[1440px] space-y-6">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <Button variant="ghost" size="sm" onClick={() => navigate(backToListPath)}>
            <svg
              className="w-4 h-4 mr-1"
              fill="none"
              viewBox="0 0 24 24"
              stroke="currentColor"
              strokeWidth={2}
            >
              <path strokeLinecap="round" strokeLinejoin="round" d="M15 19l-7-7 7-7" />
            </svg>
            返回
          </Button>
          <div>
            <h1 className="text-2xl font-bold tracking-tight">{account.name || '—'}</h1>
            <p className="text-sm text-muted-foreground mt-0.5">
              {channelTypeLabel(account.type)} · {bindingModeLabel(account.bindingMode)}
            </p>
          </div>
        </div>
        <div className="flex items-center gap-2">
          {account.disabled ? (
            <Button
              variant="outline"
              size="sm"
              disabled={enableMutation.isPending}
              onClick={() => enableMutation.mutate()}
            >
              {enableMutation.isPending && <LoaderIcon className="mr-2 h-4 w-4 animate-spin" />}
              启用
            </Button>
          ) : (
            <Button
              variant="outline"
              size="sm"
              disabled={disableMutation.isPending}
              onClick={() => disableMutation.mutate()}
            >
              {disableMutation.isPending && <LoaderIcon className="mr-2 h-4 w-4 animate-spin" />}
              停用
            </Button>
          )}
          <Button
            variant="ghost"
            size="icon"
            className="h-8 w-8 text-destructive"
            onClick={() => setShowDeleteDialog(true)}
          >
            <TrashIcon className="h-4 w-4" />
          </Button>
        </div>
      </div>

      <Tabs tabs={tabs} activeTab={activeTab} onChange={(id) => setActiveTab(id as TabId)} />

      <div className="animate-in fade-in duration-300">
        {activeTab === 'overview' && <OverviewTab account={account} />}
        {activeTab === 'sessions' && <SessionsTab accountId={account.id} />}
        {activeTab === 'bindings' && <UserBindingsTab accountId={account.id} />}
        {activeTab === 'jobs' && <CronJobsTab accountId={account.id} />}
      </div>

      <AlertDialog open={showDeleteDialog} onOpenChange={setShowDeleteDialog}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>确认删除</AlertDialogTitle>
            <AlertDialogDescription>
              将删除该渠道账号并通知 Worker 停止，不可撤销。
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>取消</AlertDialogCancel>
            <AlertDialogAction
              className="bg-destructive hover:bg-destructive/90"
              onClick={() => deleteMutation.mutate()}
            >
              {deleteMutation.isPending && <LoaderIcon className="mr-2 h-4 w-4 animate-spin" />}
              删除
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
